/**
 * CollectionOverviewPanel.tsx — the collection detail body from the VER-128 canvas (artboards
 * 1a and 3a/2a step 3): stats bar, navy curriculum-overview card, learning-transfer/impact
 * cards, and the stories table. Shared between the standalone `/preview/collection/:id`
 * overview and step 3 of the create-programme stepper so the two never drift apart.
 *
 * The caller owns page-level chrome (breadcrumb, "Select this collection" action, step
 * eyebrow) — this component renders only the content both places show identically.
 */
import React from "react";
import {
    Box,
    Chip,
    Grid,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import GroupsIcon from "@mui/icons-material/Groups";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { CollectionOverview } from "./createProgramme.types";

const SECTORAL_ICONS: Record<string, React.ReactElement> = {
    "Primary school": <SchoolIcon sx={{ fontSize: 15 }} />,
    "Youth group": <GroupsIcon sx={{ fontSize: 15 }} />,
};

const StatBlock: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Stack spacing={0.75}>
        <Typography
            variant="overline"
            color="text.secondary"
            sx={{ lineHeight: 1.35, minHeight: 30, display: "block" }}
        >
            {label}
        </Typography>
        {children}
    </Stack>
);

export const CollectionOverviewPanel: React.FC<{ collection: CollectionOverview }> = ({ collection }) => {
    return (
        <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 3 }}>
                <Grid container spacing={4}>
                    <Grid item xs={6} sm={2}>
                        <StatBlock label="Stories in collection">
                            <Typography variant="subtitle1" color="secondary.main">
                                {collection.storyCount}
                            </Typography>
                        </StatBlock>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <StatBlock label="Age range">
                            <Typography variant="subtitle1" color="secondary.main">
                                {collection.ageRange}
                            </Typography>
                        </StatBlock>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <StatBlock label="Sectoral application">
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {collection.sectoralApplications.map((label) => (
                                    <Chip
                                        key={label}
                                        size="small"
                                        color="info"
                                        icon={SECTORAL_ICONS[label]}
                                        label={label}
                                        sx={{ textTransform: "none" }}
                                    />
                                ))}
                            </Stack>
                        </StatBlock>
                    </Grid>
                    <Grid item xs={12}>
                        <StatBlock label="Curriculum keywords">
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {collection.curriculumKeywords.map((label) => (
                                    <Chip key={label} size="small" label={label} sx={{ textTransform: "none" }} />
                                ))}
                            </Stack>
                        </StatBlock>
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ p: 3.25, borderRadius: 1.5, bgcolor: "secondary.main", color: "common.white" }}>
                <Stack spacing={1.5}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={2}
                        flexWrap="wrap"
                    >
                        <Typography variant="h6" color="common.white">
                            Curriculum overview
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={1} color="primary.main">
                            <MenuBookIcon sx={{ fontSize: 18 }} />
                            <Typography variant="body2" fontWeight={700} color="primary.main">
                                {collection.storyCount} stories · {collection.sessionCount} sessions
                            </Typography>
                        </Stack>
                    </Stack>
                    <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.86)", lineHeight: 1.75 }}>
                        {collection.curriculumOverview}
                    </Typography>
                </Stack>
            </Box>

            <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
                        <Stack spacing={1.25}>
                            <Typography variant="h6" color="secondary.main">
                                Collection learning transfer
                            </Typography>
                            <Typography variant="body2" color="secondary.dark" sx={{ lineHeight: 1.7 }}>
                                {collection.learningTransfer}
                            </Typography>
                        </Stack>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 3, height: "100%" }}>
                        <Stack spacing={1.25}>
                            <Typography variant="h6" color="secondary.main">
                                Collection impact
                            </Typography>
                            <Typography variant="body2" color="secondary.dark" sx={{ lineHeight: 1.7 }}>
                                {collection.impact}
                            </Typography>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ px: 3, pt: 2.5, pb: 2 }}
                >
                    <Typography variant="h6" color="secondary.main">
                        Stories in this collection
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {collection.storyCount} stories
                    </Typography>
                </Stack>
                <Table size="small" sx={{ display: { xs: "none", sm: "table" } }}>
                    <TableHead sx={{ bgcolor: "secondaryShade.main" }}>
                        <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>Discussion theme</TableCell>
                            <TableCell>Learning objective</TableCell>
                            <TableCell>Learning transfer</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {collection.stories.map((row) => (
                            <TableRow key={row.position}>
                                <TableCell>
                                    <Stack direction="row" spacing={1.25} alignItems="baseline">
                                        <Typography variant="body2" color="primary.main" fontWeight={700}>
                                            {row.position}
                                        </Typography>
                                        <Typography variant="body2" color="secondary.main" fontWeight={700}>
                                            {row.title}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="secondary.dark">
                                        {row.theme}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="secondary.dark">
                                        {row.learningObjective}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="secondary.dark">
                                        {row.learningTransfer}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {/* Small screens: the 4-column table stops being readable, so each story becomes a
                    stacked card instead — same data, same field order. */}
                <Stack
                    divider={<Box sx={{ borderTop: "1px solid", borderColor: "borderShade.main" }} />}
                    sx={{ display: { xs: "flex", sm: "none" } }}
                >
                    {collection.stories.map((row) => (
                        <Stack key={row.position} spacing={1} sx={{ p: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="baseline">
                                <Typography variant="body2" color="primary.main" fontWeight={700}>
                                    {row.position}
                                </Typography>
                                <Typography variant="body2" color="secondary.main" fontWeight={700}>
                                    {row.title}
                                </Typography>
                            </Stack>
                            <Stack spacing={0.25}>
                                <Typography variant="overline" color="text.secondary">
                                    Discussion theme
                                </Typography>
                                <Typography variant="body2" color="secondary.dark">
                                    {row.theme}
                                </Typography>
                            </Stack>
                            <Stack spacing={0.25}>
                                <Typography variant="overline" color="text.secondary">
                                    Learning objective
                                </Typography>
                                <Typography variant="body2" color="secondary.dark">
                                    {row.learningObjective}
                                </Typography>
                            </Stack>
                            <Stack spacing={0.25}>
                                <Typography variant="overline" color="text.secondary">
                                    Learning transfer
                                </Typography>
                                <Typography variant="body2" color="secondary.dark">
                                    {row.learningTransfer}
                                </Typography>
                            </Stack>
                        </Stack>
                    ))}
                </Stack>
            </Paper>
        </Stack>
    );
};
